import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { VKUser } from '../types';
import { showAlert, hapticFeedback } from '../lib/vk-bridge';

interface UploadReceiptProps {
  currentUser: VKUser | null;
}

// URL OCR API
const OCR_API_URL = process.env.REACT_APP_OCR_API_URL || process.env.REACT_APP_PROXY_URL || 'https://api.testagentn8n.ru';

const UploadReceipt: React.FC<UploadReceiptProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState<'select' | 'confirm' | 'processing'>('select');
  const [progressText, setProgressText] = useState('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Проверяем тип файла
    if (!file.type.startsWith('image/')) {
      showAlert('Пожалуйста, выберите изображение');
      return;
    }

    // Проверяем размер (макс 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showAlert('Файл слишком большой. Максимум 10 МБ.');
      return;
    }

    setSelectedFile(file);

    // Создаём превью
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target?.result as string);
      setStep('confirm');
    };
    reader.readAsDataURL(file);
    
    hapticFeedback.impact('light');
  };

  const handleRetake = () => {
    setSelectedImage(null);
    setSelectedFile(null);
    setStep('select');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirm = async () => {
    if (!selectedFile || !currentUser) return;

    setStep('processing');
    setProcessing(true);
    hapticFeedback.impact('medium');

    try {
      // Шаг 1: Загружаем фото
      setProgressText('Загружаем фото...');
      
      const formData = new FormData();
      formData.append('receipt', selectedFile);
      formData.append('user_id', String(currentUser.id));
      formData.append('user_name', currentUser.first_name);
      formData.append('platform', 'vk');

      // Шаг 2: Отправляем на OCR API
      setProgressText('Распознаём чек...');
      
      const response = await fetch(`${OCR_API_URL}/api/ocr/receipt`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Ошибка сервера: ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      // Шаг 3: Получаем ID созданной доски
      setProgressText('Создаём доску...');

      const boardId = result.data?.board_id || result.board_id;

      if (!boardId) {
        throw new Error('Не удалось создать доску');
      }

      hapticFeedback.notification('success');
      setProgressText('Готово! Переходим к доске...');

      // Переходим к доске
      setTimeout(() => {
        navigate(`/board/${boardId}`);
      }, 500);

    } catch (error: any) {
      console.error('❌ Ошибка обработки чека:', error);
      hapticFeedback.notification('error');
      showAlert(`Ошибка: ${error.message || 'Не удалось обработать чек'}`);
      setStep('confirm');
      setProcessing(false);
    }
  };

  return (
    <>
      <style>{`
        .upload-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px 20px;
          background: var(--vk-bg, #f5f5f5);
          color: var(--vk-text, #000);
          box-sizing: border-box;
        }
        .upload-title {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          text-align: center;
        }
        .upload-subtitle {
          font-size: 15px;
          color: var(--vk-hint, #818c99);
          text-align: center;
          margin-bottom: 32px;
          line-height: 1.4;
        }
        .upload-area {
          width: 100%;
          max-width: 360px;
          aspect-ratio: 3/4;
          border: 3px dashed var(--vk-accent, #4680C2);
          border-radius: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          background: var(--vk-card, #fff);
          position: relative;
          overflow: hidden;
        }
        .upload-area:active {
          transform: scale(0.98);
          border-color: #3570b0;
        }
        .upload-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }
        .upload-hint {
          font-size: 16px;
          font-weight: 500;
          color: var(--vk-accent, #4680C2);
        }
        .upload-hint-small {
          font-size: 13px;
          color: var(--vk-hint, #818c99);
          margin-top: 8px;
        }
        .preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
          border-radius: 17px;
        }
        .file-input-hidden {
          display: none;
        }
        .confirm-actions {
          width: 100%;
          max-width: 360px;
          margin-top: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .btn-primary {
          width: 100%;
          padding: 16px;
          border-radius: 12px;
          background: var(--vk-accent, #4680C2);
          color: #fff;
          font-size: 17px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.15s;
          box-shadow: 0 4px 12px rgba(70, 128, 194, 0.3);
        }
        .btn-primary:active {
          transform: scale(0.98);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-secondary {
          width: 100%;
          padding: 14px;
          border-radius: 12px;
          background: transparent;
          color: var(--vk-accent, #4680C2);
          font-size: 15px;
          font-weight: 500;
          border: none;
          cursor: pointer;
        }
        .processing-card {
          width: 100%;
          max-width: 360px;
          background: var(--vk-card, #fff);
          border-radius: 20px;
          padding: 40px 24px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        .processing-spinner {
          font-size: 48px;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .processing-text {
          font-size: 16px;
          font-weight: 500;
          color: var(--vk-text, #000);
        }
        .processing-hint {
          font-size: 14px;
          color: var(--vk-hint, #818c99);
          margin-top: 8px;
        }
      `}</style>

      <div className="upload-page">
        <div className="upload-title">📸 Загрузить чек</div>
        <div className="upload-subtitle">
          Сфотографируйте чек или выберите фото из галереи.<br/>
          Мы распознаем все позиции автоматически.
        </div>

        {step === 'processing' ? (
          <div className="processing-card">
            <div className="processing-spinner">🍽️</div>
            <div className="processing-text">{progressText}</div>
            <div className="processing-hint">Подождите 10–20 секунд</div>
          </div>
        ) : (
          <>
            <div 
              className="upload-area"
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedImage ? (
                <img src={selectedImage} alt="Превью чека" className="preview-image" />
              ) : (
                <>
                  <div className="upload-icon">📷</div>
                  <div className="upload-hint">Нажмите для загрузки</div>
                  <div className="upload-hint-small">JPG, PNG до 10 МБ</div>
                </>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="file-input-hidden"
            />

            {step === 'confirm' && (
              <div className="confirm-actions">
                <button 
                  className="btn-primary" 
                  onClick={handleConfirm}
                  disabled={processing}
                >
                  ✅ Распознать чек
                </button>
                <button className="btn-secondary" onClick={handleRetake}>
                  📸 Выбрать другое фото
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UploadReceipt;
